import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDarkMode?: boolean;
}

type FeedbackType = "feature_request" | "bug_report" | "general_feedback";

export function FeedbackDialog({ open, onOpenChange, isDarkMode = true }: FeedbackDialogProps) {
  const { toast } = useToast();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>("feature_request");
  const [message, setMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    
    // Create the email with a properly formatted subject and body
    const subject = encodeURIComponent("JobPin Feedback");
    const body = encodeURIComponent(
      `Feedback Type: ${feedbackType.replace(/_/g, ' ')}\n\n${message}`
    );
    
    // Open the default email client with pre-filled fields
    window.open(`mailto:hi@stefanmp.com?subject=${subject}&body=${body}`);
    
    toast({
      title: "Thank you for your feedback!",
      description: "Your email client has been opened with your feedback details.",
    });
    
    setMessage("");
    setFeedbackType("feature_request");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        "sm:max-w-[425px]",
        isDarkMode ? "bg-[#2C2E2D] border-[#818881] text-[#D9D9D9]" : "bg-white"
      )}>
        <DialogHeader>
          <DialogTitle className={cn(
            "text-xl font-semibold",
            isDarkMode ? "text-[#D9D9D9]" : "text-gray-900"
          )}>
            Send Feedback
          </DialogTitle>
          <DialogDescription className={cn(
            isDarkMode ? "text-[#818881]" : "text-gray-500"
          )}>
            Help us improve JobPin by sharing your thoughts.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="type" className={isDarkMode ? "text-[#D9D9D9]" : ""}>
              Feedback Type
            </Label>
            <Select
              value={feedbackType}
              onValueChange={(value: FeedbackType) => setFeedbackType(value)}
            >
              <SelectTrigger className={cn(
                isDarkMode ? "bg-[#333433] border-[#818881] text-[#D9D9D9]" : "bg-white"
              )}>
                <SelectValue placeholder="Select feedback type" />
              </SelectTrigger>
              <SelectContent className={cn(
                isDarkMode ? "bg-[#2C2E2D] border-[#818881]" : "bg-white"
              )}>
                <SelectItem value="feature_request" className={cn(
                  isDarkMode ? "text-[#D9D9D9] hover:bg-[#818881]/20 focus:bg-[#818881]/20" : ""
                )}>
                  Feature Request
                </SelectItem>
                <SelectItem value="bug_report" className={cn(
                  isDarkMode ? "text-[#D9D9D9] hover:bg-[#818881]/20 focus:bg-[#818881]/20" : ""
                )}>
                  Bug Report
                </SelectItem>
                <SelectItem value="general_feedback" className={cn(
                  isDarkMode ? "text-[#D9D9D9] hover:bg-[#818881]/20 focus:bg-[#818881]/20" : ""
                )}>
                  General Feedback
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className={isDarkMode ? "text-[#D9D9D9]" : ""}>
              Your Message
            </Label>
            <Textarea
              id="message"
              placeholder="Describe your feature request or feedback..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={cn(
                "min-h-[120px]",
                isDarkMode ? "bg-[#333433] border-[#818881] text-[#D9D9D9] placeholder:text-[#818881]" : "bg-white"
              )}
            />
          </div>
          <div className="flex justify-between pt-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className={cn(
                isDarkMode 
                  ? "bg-[#333433] text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] border-[#818881]"
                  : "text-gray-600 hover:bg-[#CCF751]/90 hover:text-[#2C2E2D]"
              )}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={!message.trim()}
              className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
            >
              <Mail className="h-4 w-4 mr-2" />
              Submit Feedback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 